import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Copy, Check, Download, FileText, Phone, Mail, CreditCard, Loader2, AlertTriangle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BANK_PAYMENT_METHODS = new Set(["qr", "bank_transfer"]);

export default function OrderSuccessPage() {
  const { t, lang } = useLanguage();
  const { orderNumber: orderNumberFromUrl } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const canceled = searchParams.get("canceled");
  const payStripe = searchParams.get("pay") === "stripe";
  const stripeErrorFlag = searchParams.get("stripe_error") === "1";
  const tokenFromUrl = searchParams.get("token") || "";

  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState("");
  const [stripeStatus, setStripeStatus] = useState(sessionId ? "polling" : null); // polling | paid | failed | expired | pending
  const [retrying, setRetrying] = useState(false);
  const [contact, setContact] = useState({
    name: "Petra Elisherová",
    phone: "+420 608 838 058",
    email: "eshoprebelky@gmail.com",
  });

  useEffect(() => {
    let stored = null;
    try {
      const raw = sessionStorage.getItem("rebelky_last_order");
      if (raw) {
        stored = JSON.parse(raw);
        setOrder(stored);
      }
    } catch {
      /* ignore session storage parse */
    }
    axios.get(`${API}/settings`).then(res => {
      const s = res.data || {};
      if (s.contact_name || s.contact_phone || s.contact_email) {
        setContact(c => ({
          name: s.contact_name || c.name,
          phone: s.contact_phone || c.phone,
          email: s.contact_email || c.email,
        }));
      }
    }).catch(() => {});

    // Hydrate from server receipt when we have order number + token (refresh-safe / SMS deep-link)
    const on = orderNumberFromUrl || stored?.order_number;
    const token = tokenFromUrl || stored?.invoice_token || "";
    if (on && token) {
      axios.get(`${API}/orders/${on}/receipt`, { params: { token } })
        .then(r => {
          const receipt = r.data || {};
          setOrder(prev => {
            const next = {
              ...(prev || {}),
              ...receipt,
              _id: receipt._id || prev?._id || "",
              invoice_token: receipt.invoice_token || token,
              // Prefer live Stripe URL/QR from receipt; keep error from session if any
              stripe_url: receipt.stripe_pay_url || receipt.stripe_checkout_url || prev?.stripe_url || "",
              stripe_checkout_url: receipt.stripe_checkout_url || prev?.stripe_checkout_url || "",
              stripe_qr_png_base64: receipt.stripe_qr_png_base64 || prev?.stripe_qr_png_base64 || "",
              stripe_error: stripeErrorFlag
                ? (prev?.stripe_error || (lang === "cz"
                  ? "Platební bránu Stripe se nepodařilo spustit. Zkus Zaplatit znovu."
                  : "Stripe Checkout could not be started. Please retry Pay."))
                : (prev?.stripe_error || null),
            };
            try {
              sessionStorage.setItem("rebelky_last_order", JSON.stringify(next));
            } catch {
              /* ignore */
            }
            return next;
          });
        })
        .catch(() => {});
    }
  }, [orderNumberFromUrl, tokenFromUrl, stripeErrorFlag, lang]);

  const paymentMethod = (order?.payment_method || "").toLowerCase();
  const isStripePay = payStripe
    || paymentMethod === "card_online"
    || !!order?.stripe_url
    || !!order?.stripe_checkout_url
    || !!sessionId
    || stripeErrorFlag
    || !!order?.stripe_error;

  // Bank SPAYD QR ONLY when customer chose bank/QR — never as Stripe fallback
  const showBankQr = !isStripePay && (
    BANK_PAYMENT_METHODS.has(paymentMethod)
    || (!paymentMethod && !payStripe && !stripeErrorFlag && !!(order?.qr_png_base64 || order?.iban))
  );

  const stripeQr = order?.stripe_qr_png_base64 || "";
  const stripeUrl = order?.stripe_url || order?.stripe_checkout_url || "";
  const orderPaid = !!(order?.is_paid) || stripeStatus === "paid";
  const pdfLabel = orderPaid
    ? (lang === "cz" ? "STÁHNOUT FAKTURU (PDF)" : "DOWNLOAD INVOICE (PDF)")
    : (lang === "cz" ? "STÁHNOUT OBJEDNÁVKU (PDF)" : "DOWNLOAD ORDER (PDF)");
  const stripeError = order?.stripe_error || (stripeErrorFlag && !stripeUrl
    ? (lang === "cz"
      ? "Platební bránu Stripe se nepodařilo spustit. Objednávka je uložená, ale platba kartou neproběhla."
      : "Stripe Checkout could not be started. Your order is saved, but card payment did not run.")
    : "");

  // Poll Stripe status after redirect from Stripe Checkout
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const maxAttempts = 8;
    const intervalMs = 1800;
    let timer = null;

    const poll = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/orders/stripe-status/${sessionId}`);
        if (r.data?.payment_status === "paid") {
          setStripeStatus("paid");
          return;
        }
        if (r.data?.status === "expired") {
          setStripeStatus("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setStripeStatus("pending");
          return;
        }
        timer = setTimeout(poll, intervalMs);
      } catch {
        if (attempts >= maxAttempts) {
          setStripeStatus("failed");
          return;
        }
        timer = setTimeout(poll, intervalMs);
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId]);

  const retryStripe = async () => {
    // Prefer order._id from receipt (works without sessionStorage on phone deep-links)
    const oid = order?._id;
    if (!oid) return;
    setRetrying(true);
    try {
      const stripeRes = await axios.post(`${API}/orders/${oid}/stripe-session`, {
        origin_url: window.location.origin,
      });
      if (!stripeRes.data?.url) throw new Error("Stripe nevrátil URL");
      const tok = order.invoice_token || tokenFromUrl || "";
      const next = {
        ...order,
        _id: oid,
        payment_method: "card_online",
        stripe_url: stripeRes.data.pay_url || stripeRes.data.url,
        stripe_checkout_url: stripeRes.data.url,
        stripe_session_id: stripeRes.data.session_id,
        stripe_qr_png_base64: stripeRes.data.qr_png_base64 || "",
        stripe_error: null,
        qr_png_base64: "",
        iban: "",
        invoice_token: tok,
      };
      setOrder(next);
      try {
        sessionStorage.setItem("rebelky_last_order", JSON.stringify(next));
      } catch {
        /* ignore */
      }
      const q = new URLSearchParams({ pay: "stripe" });
      if (tok) q.set("token", tok);
      window.history.replaceState({}, "", `/objednavka-prijata/${order.order_number || ""}?${q.toString()}`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "neznámá chyba";
      setOrder(prev => ({
        ...prev,
        stripe_error: typeof detail === "string" ? detail : JSON.stringify(detail),
        qr_png_base64: "",
        iban: "",
      }));
    } finally {
      setRetrying(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1800);
  };

  return (
    <div style={{ background: "#050505", minHeight: "100vh" }}>
      <SEO title="Objednávka přijata – Děkujeme | Rebelky" url="/objednavka-prijata" noindex />
      <Navbar />
      <div style={{ paddingTop: "5rem" }}>
        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="text-center mb-10">
            <CheckCircle size={64} style={{ color: "#00E5FF", margin: "0 auto 1.2rem" }} data-testid="success-icon" />
            <h1
              data-testid="success-title"
              style={{
                fontFamily: "Unbounded, sans-serif",
                fontWeight: 900,
                fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
                color: "#FFFFFF",
                textTransform: "uppercase",
                letterSpacing: "-0.03em",
                marginBottom: "0.8rem",
              }}
            >
              {t.success.title}
            </h1>
            <p style={{ color: "#A1A1AA", fontSize: "0.95rem", lineHeight: 1.6 }}>
              {lang === "cz"
                ? (isStripePay
                  ? (stripeError
                    ? "Objednávka je přijatá, ale platba kartou se nespustila — viz chyba níže."
                    : "Děkujeme za objednávku! Dokonči platbu tlačítkem Zaplatit níže.")
                  : "Děkujeme za objednávku! Email s rekapitulací a QR kódem najdeš ve své schránce.")
                : (isStripePay
                  ? (stripeError
                    ? "Order received, but card payment did not start — see the error below."
                    : "Thanks for your order! Complete payment with the Pay button below.")
                  : "Thanks for your order! A confirmation email with the QR code is on its way.")}
            </p>
          </div>

          {/* Stripe ERROR — never show bank QR here */}
          {isStripePay && stripeError && !sessionId && stripeStatus !== "paid" && !stripeUrl && (
            <div
              data-testid="stripe-error-block"
              className="glass-card rounded-2xl mb-6"
              style={{
                padding: "1.5rem",
                background: "rgba(255,0,90,0.07)",
                border: "1px solid rgba(255,0,90,0.45)",
              }}
            >
              <div style={{ display: "flex", gap: "0.9rem", alignItems: "flex-start" }}>
                <AlertTriangle className="w-6 h-6 text-[#FF005A] shrink-0" />
                <div style={{ flex: 1 }}>
                  <p style={{
                    fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem", letterSpacing: "0.15em",
                    textTransform: "uppercase", fontWeight: 800, color: "#FF005A", marginBottom: "0.4rem",
                  }}>
                    {lang === "cz" ? "Platba kartou se nezdařila" : "Card payment failed"}
                  </p>
                  <p data-testid="stripe-error-message" style={{ color: "#FFC9D9", fontSize: "0.85rem", lineHeight: 1.55 }}>
                    {stripeError}
                  </p>
                  <p style={{ color: "#A1A1AA", fontSize: "0.78rem", marginTop: "0.6rem", lineHeight: 1.5 }}>
                    {lang === "cz"
                      ? "Bankovní QR převod se automaticky nespouští. Zkus platbu kartou znovu, nebo nás kontaktuj."
                      : "We do not fall back to bank QR automatically. Retry card payment or contact us."}
                  </p>
                  {order?._id && (
                    <button
                      type="button"
                      data-testid="stripe-retry-btn"
                      onClick={retryStripe}
                      disabled={retrying}
                      className="btn-rebel rounded-none mt-4 flex items-center justify-center gap-2"
                      style={{ fontSize: "0.65rem", padding: "0.75rem 1.2rem", width: "100%", border: "none", cursor: retrying ? "wait" : "pointer" }}
                    >
                      <CreditCard size={14} /> {retrying
                        ? (lang === "cz" ? "SPOUŠTÍM…" : "STARTING…")
                        : (lang === "cz" ? "ZKUSIT PLATBU KARTOU ZNOVU" : "RETRY CARD PAYMENT")}
                    </button>
                  )}
                  {order?.order_number && (
                    <p style={{ color: "#52525B", fontSize: "0.72rem", marginTop: "0.8rem" }}>
                      {lang === "cz" ? "Číslo objednávky" : "Order #"}: {order.order_number}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Stripe card payment — Zaplatit CTA + large mobile QR (short /pay URL) */}
          {isStripePay && !sessionId && stripeStatus !== "paid" && (stripeQr || stripeUrl) && !stripeError && (
            <div
              data-testid="stripe-pay-block"
              className="glass-card rounded-2xl mb-6"
              style={{
                padding: "1.75rem 1.5rem 1.5rem",
                background: "#0A0A0A",
                border: "1px solid rgba(0,229,255,0.35)",
                boxShadow: "0 0 40px rgba(0,229,255,0.08)",
              }}
            >
              <p style={{
                fontFamily: "Unbounded, sans-serif", fontSize: "0.55rem", color: "#00E5FF",
                letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: "0.9rem",
              }}>
                ★ {lang === "cz" ? "PLATBA KARTOU (STRIPE)" : "CARD PAYMENT (STRIPE)"} ★
              </p>

              {order?.order_number && (
                <p style={{ color: "#A1A1AA", fontSize: "0.8rem", marginBottom: "1.1rem" }}>
                  {lang === "cz" ? "Objednávka" : "Order"} #{order.order_number}
                  {order.total != null ? ` · ${Math.round(order.total)} ${order.currency || "CZK"}` : ""}
                </p>
              )}

              {stripeUrl && (
                <a
                  data-testid="stripe-pay-btn"
                  href={stripeUrl}
                  className="btn-rebel rounded-none w-full flex items-center justify-center gap-3"
                  style={{
                    fontSize: "clamp(0.85rem, 2.5vw, 1.05rem)",
                    fontWeight: 800,
                    letterSpacing: "0.12em",
                    padding: "1.15rem 1.5rem",
                    textAlign: "center",
                    textDecoration: "none",
                    textTransform: "uppercase",
                  }}
                >
                  <CreditCard size={22} /> {lang === "cz" ? "Zaplatit" : "Pay"}
                </a>
              )}

              <p style={{ color: "#52525B", fontSize: "0.72rem", textAlign: "center", marginTop: "0.85rem", lineHeight: 1.45 }}>
                {lang === "cz"
                  ? "Otevře bezpečnou platební bránu Stripe (karta / Apple Pay)."
                  : "Opens secure Stripe Checkout (card / Apple Pay)."}
              </p>

              {stripeQr && stripeUrl ? (
                <div
                  data-testid="stripe-qr-mobile"
                  style={{
                    marginTop: "1.35rem",
                    paddingTop: "1.2rem",
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    textAlign: "center",
                  }}
                >
                  <p style={{
                    fontFamily: "Unbounded, sans-serif", fontSize: "0.55rem", color: "#00E5FF",
                    letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: "0.75rem",
                  }}>
                    {lang === "cz" ? "Dokonči platbu na mobilu" : "Finish payment on mobile"}
                  </p>
                  <a
                    href={stripeUrl}
                    title={lang === "cz"
                      ? "Naskenuj mobilním telefonem — Stripe Checkout (karta / Apple Pay)"
                      : "Scan with your phone — Stripe Checkout (card / Apple Pay)"}
                    aria-label={lang === "cz" ? "QR kód platby Stripe" : "Stripe payment QR code"}
                    style={{
                      display: "inline-block",
                      background: "#FFFFFF",
                      padding: "12px",
                      borderRadius: "10px",
                      lineHeight: 0,
                      border: "1px solid rgba(0,229,255,0.45)",
                      boxShadow: "0 0 20px rgba(0,229,255,0.2)",
                    }}
                  >
                    <img
                      data-testid="stripe-qr-image"
                      src={`data:image/png;base64,${stripeQr}`}
                      alt={lang === "cz"
                        ? "QR kód otevírá platební bránu Stripe na mobilu"
                        : "QR code opens Stripe Checkout on mobile"}
                      width={180}
                      height={180}
                      style={{ width: "180px", height: "180px", display: "block", aspectRatio: "1 / 1", objectFit: "contain" }}
                    />
                  </a>
                  <p style={{ color: "#A1A1AA", fontSize: "0.72rem", marginTop: "0.75rem", lineHeight: 1.45 }}>
                    {lang === "cz"
                      ? "Naskenuj QR telefonem — zaplatíš kartou nebo Apple Pay."
                      : "Scan with your phone — pay by card or Apple Pay."}
                  </p>
                  <div
                    style={{
                      marginTop: "0.85rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <a
                      data-testid="stripe-pay-link"
                      href={stripeUrl}
                      style={{
                        color: "#00E5FF",
                        fontSize: "0.72rem",
                        wordBreak: "break-all",
                        maxWidth: "100%",
                        textDecoration: "underline",
                      }}
                      title={lang === "cz" ? "Otevřít platbu na mobilu" : "Open payment on mobile"}
                    >
                      {lang === "cz" ? "Odkaz na platbu kartou / Apple Pay" : "Card / Apple Pay link"}
                    </a>
                    <button
                      type="button"
                      data-testid="stripe-pay-link-copy"
                      onClick={() => copy(stripeUrl, "stripe-pay")}
                      title={lang === "cz" ? "Kopírovat odkaz na platbu" : "Copy payment link"}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(0,229,255,0.35)",
                        color: copied === "stripe-pay" ? "#00E5FF" : "#A1A1AA",
                        padding: "0.25rem 0.55rem",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontFamily: "Unbounded, sans-serif",
                        fontSize: "0.5rem",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      {copied === "stripe-pay"
                        ? (lang === "cz" ? "Zkopírováno" : "Copied")
                        : (lang === "cz" ? "Kopírovat" : "Copy")}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Stripe payment status banner */}
          {stripeStatus && (
            <div
              data-testid="stripe-status-banner"
              className="glass-card rounded-2xl mb-6"
              style={{
                padding: "1.3rem 1.5rem",
                background: stripeStatus === "paid" ? "rgba(0,255,100,0.07)" : (canceled || stripeStatus === "expired" || stripeStatus === "failed") ? "rgba(255,0,90,0.07)" : "rgba(0,229,255,0.06)",
                border: "1px solid " + (stripeStatus === "paid" ? "rgba(0,255,100,0.4)" : (canceled || stripeStatus === "expired" || stripeStatus === "failed") ? "rgba(255,0,90,0.4)" : "rgba(0,229,255,0.35)"),
                display: "flex", alignItems: "center", gap: "1rem",
              }}
            >
              {stripeStatus === "paid" ? <CheckCircle className="w-6 h-6 text-[#00FF64]" /> :
               stripeStatus === "polling" ? <Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" /> :
               <CreditCard className="w-6 h-6 text-[#FF005A]" />}
              <div style={{ flex: 1 }}>
                <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 800, color: stripeStatus === "paid" ? "#00FF64" : "#FFF" }}>
                  {stripeStatus === "paid" && "✓ Platba úspěšná"}
                  {stripeStatus === "polling" && "Ověřuji platbu u banky…"}
                  {stripeStatus === "pending" && "Platba zpracovává — čekej e-mail"}
                  {stripeStatus === "expired" && "Platba vypršela"}
                  {stripeStatus === "failed" && "Platba se nezdařila"}
                </p>
                <p style={{ color: "#A1A1AA", fontSize: "0.8rem", marginTop: "0.3rem" }}>
                  {stripeStatus === "paid" && "Děkujeme! Tvoje objednávka je zaplacená, brzy ti doručíme zboží."}
                  {stripeStatus === "polling" && "Toto trvá obvykle pár sekund. Nezavírej stránku, prosím."}
                  {stripeStatus === "pending" && "Pokud platba projde do hodiny, dostaneš e-mail. V opačném případě tě budeme kontaktovat."}
                  {(stripeStatus === "expired" || stripeStatus === "failed") && "Zkus platbu kartou znovu přes odkaz v e-mailu nebo nás kontaktuj. Bankovní QR se automaticky nespouští."}
                </p>
              </div>
            </div>
          )}
          {canceled && !stripeStatus && (
            <div data-testid="stripe-canceled-banner" className="glass-card rounded-2xl mb-6" style={{ padding: "1.3rem 1.5rem", background: "rgba(255,165,0,0.05)", border: "1px solid rgba(255,165,0,0.4)" }}>
              <p style={{ color: "#FFA500", fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 800 }}>Platba zrušena</p>
              <p style={{ color: "#A1A1AA", fontSize: "0.8rem", marginTop: "0.3rem" }}>
                Tvoje objednávka je vytvořená. Pro platbu kartou se vrať do e-mailu / kontaktuj nás — bankovní QR se automaticky nespouští.
              </p>
            </div>
          )}

          {order ? (
            <>
              {showBankQr && (
                <div
                  data-testid="qr-payment-block"
                  className="glass-card rounded-2xl"
                  style={{
                    padding: "2rem",
                    background: "#0A0A0A",
                    border: "1px solid rgba(255,0,90,0.25)",
                    boxShadow: "0 0 40px rgba(255,0,90,0.08)",
                  }}
                >
                  <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.55rem", color: "#FF005A", letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: "1.2rem", textAlign: "center" }}>
                    ★ QR PLATBA — naskenuj v bance ★
                  </p>

                  {order.qr_png_base64 ? (
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: "1.5rem" }}>
                      <div
                        style={{
                          background: "#FFFFFF",
                          padding: "1rem",
                          borderRadius: "12px",
                          boxShadow: "0 0 30px rgba(255,0,90,0.3)",
                        }}
                      >
                        <img
                          data-testid="qr-image"
                          src={`data:image/png;base64,${order.qr_png_base64}`}
                          alt="QR platba"
                          style={{ width: "240px", height: "240px", display: "block" }}
                        />
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: "#FF005A", textAlign: "center", fontSize: "0.85rem", marginBottom: "1.5rem" }}>
                      {lang === "cz"
                        ? "QR kód není k dispozici – číslo účtu zatím nebylo vyplněno v adminu. Použij prosím údaje níže."
                        : "QR code unavailable – bank account not yet set in admin. Please use the details below."}
                    </p>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "0.8rem" }}>
                    <PayRow label={lang === "cz" ? "Číslo objednávky / VS" : "Order # / VS"}
                      value={order.order_number} testId="vs-row"
                      onCopy={() => copy(order.order_number, "vs")} copied={copied === "vs"} />
                    {order.iban && (
                      <PayRow label="IBAN" value={order.iban} testId="iban-row"
                        onCopy={() => copy(order.iban, "iban")} copied={copied === "iban"} />
                    )}
                    <PayRow label={lang === "cz" ? "Částka" : "Amount"}
                      value={`${Math.round(order.total)} ${order.currency || "CZK"}`}
                      testId="amount-row"
                      onCopy={() => copy(String(order.total), "amount")} copied={copied === "amount"} />
                  </div>

                  {order.qr_png_base64 && (
                    <a
                      data-testid="download-qr"
                      href={`data:image/png;base64,${order.qr_png_base64}`}
                      download={`rebelky-qr-${order.order_number}.png`}
                      className="btn-outline-rebel rounded-none w-full mt-4 flex items-center justify-center gap-2"
                      style={{ fontSize: "0.65rem", padding: "0.7rem", textAlign: "center" }}
                    >
                      <Download size={12} /> {lang === "cz" ? "STÁHNOUT QR" : "DOWNLOAD QR"}
                    </a>
                  )}

                  <a
                    data-testid="download-invoice"
                    href={`${API}/orders/${order.order_number}/invoice.pdf${order.invoice_token ? `?token=${order.invoice_token}` : ""}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={orderPaid
                      ? (lang === "cz" ? "Daňový doklad po zaplacení" : "Tax invoice after payment")
                      : (lang === "cz" ? "Potvrzení objednávky (faktura až po zaplacení)" : "Order confirmation (invoice after payment)")}
                    className="btn-rebel rounded-none w-full mt-3 flex items-center justify-center gap-2"
                    style={{ fontSize: "0.65rem", padding: "0.7rem", textAlign: "center" }}
                  >
                    <FileText size={12} /> {pdfLabel}
                  </a>
                </div>
              )}
              {!showBankQr && order.order_number && (
                <a
                  data-testid="download-invoice"
                  href={`${API}/orders/${order.order_number}/invoice.pdf${order.invoice_token ? `?token=${order.invoice_token}` : ""}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={orderPaid
                    ? (lang === "cz" ? "Daňový doklad po zaplacení" : "Tax invoice after payment")
                    : (lang === "cz" ? "Potvrzení objednávky (faktura až po zaplacení)" : "Order confirmation (invoice after payment)")}
                  className="btn-outline-rebel rounded-none w-full flex items-center justify-center gap-2"
                  style={{ fontSize: "0.65rem", padding: "0.7rem", textAlign: "center", marginTop: "0.5rem" }}
                >
                  <FileText size={12} /> {pdfLabel}
                </a>
              )}
            </>
          ) : (
            <p style={{ color: "#A1A1AA", textAlign: "center", fontSize: "0.9rem" }}>
              {lang === "cz" ? "Detaily objednávky byly odeslány na tvůj email." : "Order details have been emailed to you."}
            </p>
          )}

          {/* Contact info card */}
          <div
            data-testid="contact-card"
            style={{
              marginTop: "2rem",
              padding: "1.5rem",
              background: "linear-gradient(135deg, rgba(255,0,90,0.06) 0%, rgba(0,229,255,0.04) 100%)",
              border: "1px solid rgba(255,0,90,0.2)",
              borderRadius: "12px",
            }}
          >
            <p style={{
              fontFamily: "Unbounded, sans-serif",
              fontSize: "0.55rem",
              color: "#FF005A",
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              marginBottom: "0.8rem",
              textAlign: "center",
            }}>
              ★ {lang === "cz" ? "Potřebuješ poradit?" : "Need help?"} ★
            </p>
            <p style={{
              fontFamily: "Unbounded, sans-serif",
              fontWeight: 700,
              fontSize: "1rem",
              color: "#FFFFFF",
              textAlign: "center",
              marginBottom: "0.8rem",
            }}>
              {contact.name}
            </p>
            <div style={{ display: "flex", gap: "1.5rem", justifyContent: "center", flexWrap: "wrap" }}>
              <a
                data-testid="contact-phone"
                href={`tel:${contact.phone.replace(/\s/g, "")}`}
                style={{ color: "#A1A1AA", fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: "0.4rem", textDecoration: "none" }}
                className="hover:text-white"
              >
                <Phone size={14} style={{ color: "#FF005A" }} /> {contact.phone}
              </a>
              <a
                data-testid="contact-email"
                href={`mailto:${contact.email}`}
                style={{ color: "#A1A1AA", fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: "0.4rem", textDecoration: "none" }}
                className="hover:text-white"
              >
                <Mail size={14} style={{ color: "#FF005A" }} /> {contact.email}
              </a>
            </div>
          </div>

          <div className="flex gap-4 flex-wrap justify-center mt-8">
            <Link to="/" data-testid="back-home-btn" className="btn-rebel rounded-none">{t.success.backHome}</Link>
            <Link to="/merch" data-testid="continue-shopping-btn" className="btn-outline-rebel rounded-none">{t.success.continueShopping}</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function PayRow({ label, value, onCopy, copied, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#050505",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "8px",
        padding: "0.7rem 1rem",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.55rem", color: "#A1A1AA", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "0.2rem" }}>
          {label}
        </p>
        <p style={{ fontFamily: "Manrope, sans-serif", fontSize: "0.9rem", color: "#FFFFFF", wordBreak: "break-all" }}>{value}</p>
      </div>
      <button
        onClick={onCopy}
        data-testid={`${testId}-copy`}
        style={{
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.1)",
          padding: "0.4rem 0.6rem",
          borderRadius: "6px",
          cursor: "pointer",
          color: copied ? "#00E5FF" : "#A1A1AA",
          flexShrink: 0,
          marginLeft: "0.8rem",
        }}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}
